import { type PointerEvent, type RefObject, useCallback, useEffect, useRef, useState } from 'react'
import {
  FLIPBOOK_DESKTOP_BREAKPOINT,
  FLIPBOOK_MAX_ZOOM,
  FLIPBOOK_MIN_ZOOM,
  FLIPBOOK_ZOOM_STEP,
} from './flipbook-constants'
import { getFlipbookPerformanceDiagnostics } from './flipbook-performance-diagnostics'

export interface PanPosition {
  readonly x: number
  readonly y: number
}

interface UseFlipbookViewportOptions {
  readonly onSwipe: (deltaX: number) => void
  readonly sessionToken: string | null
}

interface FullscreenDocument extends Document {
  readonly webkitFullscreenElement?: Element | null
  readonly webkitFullScreenElement?: Element | null
  readonly webkitExitFullscreen?: () => Promise<void> | void
  readonly webkitExitFullScreen?: () => Promise<void> | void
}

interface FullscreenElement extends HTMLElement {
  readonly webkitRequestFullscreen?: () => Promise<void> | void
  readonly webkitRequestFullScreen?: () => Promise<void> | void
}

interface FlipbookViewport {
  readonly bookRef: RefObject<HTMLDivElement | null>
  readonly handlePointerCancel: (event: PointerEvent<HTMLDivElement>) => void
  readonly handlePointerDown: (event: PointerEvent<HTMLDivElement>) => void
  readonly handlePointerMove: (event: PointerEvent<HTMLDivElement>) => void
  readonly handlePointerUp: (event: PointerEvent<HTMLDivElement>) => void
  readonly handleToggleFullscreen: () => Promise<void>
  readonly isDesktopSpread: boolean
  readonly isFullscreen: boolean
  readonly isPanning: boolean
  readonly pan: PanPosition
  readonly readerRef: RefObject<HTMLDivElement | null>
  readonly stageRef: RefObject<HTMLDivElement | null>
  readonly zoom: number
  readonly zoomIn: () => void
  readonly zoomOut: () => void
}

interface PanDragState {
  readonly clientX: number
  readonly clientY: number
  readonly maxX: number
  readonly maxY: number
  readonly position: PanPosition
}

function getBookTransform(position: PanPosition, zoom: number) {
  return `translate3d(${position.x}px, ${position.y}px, 0) scale(${zoom})`
}

function getActiveFullscreenElement(fullscreenDocument: FullscreenDocument) {
  return fullscreenDocument.fullscreenElement
    ?? fullscreenDocument.webkitFullscreenElement
    ?? fullscreenDocument.webkitFullScreenElement
    ?? null
}

function getFullscreenRequest(fullscreenElement: FullscreenElement) {
  return fullscreenElement.requestFullscreen
    ?? fullscreenElement.webkitRequestFullscreen
    ?? fullscreenElement.webkitRequestFullScreen
}

function getFullscreenExit(fullscreenDocument: FullscreenDocument) {
  return fullscreenDocument.exitFullscreen
    ?? fullscreenDocument.webkitExitFullscreen
    ?? fullscreenDocument.webkitExitFullScreen
}

export function useFlipbookViewport({
  onSwipe,
  sessionToken,
}: UseFlipbookViewportOptions): FlipbookViewport {
  const readerRef = useRef<HTMLDivElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const bookRef = useRef<HTMLDivElement>(null)
  const pointerStartXRef = useRef<number | null>(null)
  const panStartRef = useRef<PanDragState | null>(null)
  const panRef = useRef<PanPosition>({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(FLIPBOOK_MIN_ZOOM)
  const [pan, setPan] = useState<PanPosition>({ x: 0, y: 0 })
  const [isPanning, setIsPanning] = useState(false)
  const [isDesktopSpread, setIsDesktopSpread] = useState(() => window.matchMedia(`(min-width: ${FLIPBOOK_DESKTOP_BREAKPOINT}px)`).matches)
  const [isNativeFullscreen, setIsNativeFullscreen] = useState(false)
  const [isFallbackFullscreen, setIsFallbackFullscreen] = useState(false)

  const handlePointerDown = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (!event.isPrimary || !sessionToken) {
      return
    }

    if (event.target instanceof Element && event.target.closest('button')) {
      return
    }

    if (zoom > FLIPBOOK_MIN_ZOOM) {
      if (!stageRef.current || !bookRef.current) {
        return
      }

      const stageStyles = window.getComputedStyle(stageRef.current)
      const horizontalPadding = Number.parseFloat(stageStyles.paddingLeft) + Number.parseFloat(stageStyles.paddingRight)
      const verticalPadding = Number.parseFloat(stageStyles.paddingTop) + Number.parseFloat(stageStyles.paddingBottom)
      const availableWidth = Math.max(0, stageRef.current.clientWidth - horizontalPadding)
      const availableHeight = Math.max(0, stageRef.current.clientHeight - verticalPadding)
      const maxX = Math.max(0, (bookRef.current.offsetWidth * zoom - availableWidth) / 2)
      const maxY = Math.max(0, (bookRef.current.offsetHeight * zoom - availableHeight) / 2)

      event.currentTarget.setPointerCapture(event.pointerId)
      getFlipbookPerformanceDiagnostics().markPointerDown('pan')
      panStartRef.current = { clientX: event.clientX, clientY: event.clientY, maxX, maxY, position: panRef.current }
      setIsPanning(true)
      return
    }

    pointerStartXRef.current = event.clientX
  }, [sessionToken, zoom])

  const handlePointerMove = useCallback((event: PointerEvent<HTMLDivElement>) => {
    const start = panStartRef.current
    if (!start || zoom <= FLIPBOOK_MIN_ZOOM || !bookRef.current) {
      return
    }

    const nextX = start.position.x + event.clientX - start.clientX
    const nextY = start.position.y + event.clientY - start.clientY
    const nextPosition = {
      x: Math.min(start.maxX, Math.max(-start.maxX, nextX)),
      y: Math.min(start.maxY, Math.max(-start.maxY, nextY)),
    }
    getFlipbookPerformanceDiagnostics().markPointerMove('pan')

    panRef.current = nextPosition
    bookRef.current.style.transform = getBookTransform(nextPosition, zoom)
  }, [zoom])

  const handlePointerUp = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (panStartRef.current) {
      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId)
      }
      panStartRef.current = null
      setPan(panRef.current)
      getFlipbookPerformanceDiagnostics().endPointer('pan')
      setIsPanning(false)
      return
    }

    const startX = pointerStartXRef.current
    pointerStartXRef.current = null
    if (startX !== null) {
      onSwipe(event.clientX - startX)
    }
  }, [onSwipe])

  const handlePointerCancel = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId)
    }
    panStartRef.current = null
    setPan(panRef.current)
    getFlipbookPerformanceDiagnostics().endPointer('pan')
    pointerStartXRef.current = null
    setIsPanning(false)
  }, [])

  const handleToggleFullscreen = useCallback(async () => {
    if (!readerRef.current) {
      return
    }

    const fullscreenDocument = document as FullscreenDocument
    const activeFullscreenElement = getActiveFullscreenElement(fullscreenDocument)

    if (isFallbackFullscreen) {
      setIsFallbackFullscreen(false)
      return
    }

    try {
      if (activeFullscreenElement === readerRef.current) {
        const exitFullscreen = getFullscreenExit(fullscreenDocument)
        if (exitFullscreen) {
          await exitFullscreen.call(fullscreenDocument)
          setIsNativeFullscreen(false)
        }
      } else {
        const requestFullscreen = getFullscreenRequest(readerRef.current)
        if (!requestFullscreen) {
          setIsFallbackFullscreen(true)
          return
        }
        await requestFullscreen.call(readerRef.current)
        setIsNativeFullscreen(true)
      }
    } catch {
      // Some Safari versions expose the API but reject arbitrary-element fullscreen.
      // The reader-owned fallback keeps the teaching surface usable in that case.
      setIsNativeFullscreen(false)
      setIsFallbackFullscreen(true)
    }
  }, [isFallbackFullscreen])

  const zoomIn = useCallback(() => {
    setZoom((value) => Math.min(FLIPBOOK_MAX_ZOOM, value + FLIPBOOK_ZOOM_STEP))
  }, [])

  const zoomOut = useCallback(() => {
    setZoom((value) => Math.max(FLIPBOOK_MIN_ZOOM, value - FLIPBOOK_ZOOM_STEP))
  }, [])

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(min-width: ${FLIPBOOK_DESKTOP_BREAKPOINT}px)`)
    const handleMediaQueryChange = (event: MediaQueryListEvent) => setIsDesktopSpread(event.matches)
    mediaQuery.addEventListener('change', handleMediaQueryChange)
    return () => mediaQuery.removeEventListener('change', handleMediaQueryChange)
  }, [])

  useEffect(() => {
    const handleFullscreenChange = () => {
      const fullscreenDocument = document as FullscreenDocument
      setIsNativeFullscreen(getActiveFullscreenElement(fullscreenDocument) === readerRef.current)
    }

    document.addEventListener('fullscreenchange', handleFullscreenChange)
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange)
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange)
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange)
    }
  }, [])

  useEffect(() => {
    if (!isFallbackFullscreen) {
      return
    }

    const previousBodyOverflow = document.body.style.overflow
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsFallbackFullscreen(false)
      }
    }

    document.body.style.overflow = 'hidden'
    document.addEventListener('keydown', handleEscape)
    return () => {
      document.body.style.overflow = previousBodyOverflow
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isFallbackFullscreen])

  useEffect(() => {
    if (zoom === FLIPBOOK_MIN_ZOOM) {
      // A reset at 100% keeps the next zoom centered on the whole book.
      panRef.current = { x: 0, y: 0 }
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setPan({ x: 0, y: 0 })
    }
  }, [zoom])

  useEffect(() => {
    if (!sessionToken) {
      return
    }

    // A new protected session represents a new reader surface; restore the initial viewport.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setZoom(FLIPBOOK_MIN_ZOOM)
    panRef.current = { x: 0, y: 0 }
    setPan({ x: 0, y: 0 })
    panStartRef.current = null
    pointerStartXRef.current = null
  }, [sessionToken])

  return {
    bookRef,
    handlePointerCancel,
    handlePointerDown,
    handlePointerMove,
    handlePointerUp,
    handleToggleFullscreen,
    isDesktopSpread,
    isFullscreen: isNativeFullscreen || isFallbackFullscreen,
    isPanning,
    pan,
    readerRef,
    stageRef,
    zoom,
    zoomIn,
    zoomOut,
  }
}
